export const useTheme = () => {
  const route = useRoute()
  const themeFonts = computed(() => resolveThemeFonts(route.query))
  const DEFAULT_LOGO_URL = '/images/placeholder-logo.svg'

  // Read theme colors from URL parameters (passed by parent page)
  const accentColor = computed(() => {
    const rawColor = route.query.accentColor as string
    if (!rawColor) return '#5F2A2B' // Default ultramarine

    try {
      const decodedColor = decodeURIComponent(rawColor)
      return decodedColor
    } catch (error) {
      console.error('Failed to decode accent color:', rawColor, error)
      return '#5F2A2B'
    }
  })

  const textOnBlockColor = computed(() => {
    const rawColor = route.query.textOnBlockColor as string
    if (!rawColor) return '#FFFFFF'

    try {
      const decodedColor = decodeURIComponent(rawColor)
      return decodedColor
    } catch (error) {
      console.error('Failed to decode text on block color:', rawColor, error)
      return '#FFFFFF'
    }
  })

  const blockColor = computed(() => {
    const rawColor = route.query.blockColor as string
    if (!rawColor) return '#fffeff' // Default warm beige

    try {
      const decodedColor = decodeURIComponent(rawColor)
      return decodedColor
    } catch (error) {
      console.error('Failed to decode block color:', rawColor, error)
      return '#fffeff'
    }
  })

  const buttonBgColor = computed(() => {
    const rawColor = route.query.buttonBgColor as string
    if (!rawColor) return accentColor.value // Default to accent color

    try {
      const decodedColor = decodeURIComponent(rawColor)
      return decodedColor
    } catch (error) {
      console.error('Failed to decode button background color:', rawColor, error)
      return accentColor.value
    }
  })

  const buttonTextColor = computed(() => {
    const rawColor = route.query.buttonTextColor as string
    if (!rawColor) return '#FFFFFF'

    try {
      const decodedColor = decodeURIComponent(rawColor)
      return decodedColor
    } catch (error) {
      console.error('Failed to decode button text color:', rawColor, error)
      return '#FFFFFF'
    }
  })

  const buttonBorderRadius = computed(() => {
    const rawValue = route.query.buttonBorderRadius as string
    if (!rawValue) return '8px' // Default border radius

    try {
      const decodedValue = decodeURIComponent(rawValue)
      // If it's just a number, add 'px'
      if (/^\d+$/.test(decodedValue)) {
        return `${decodedValue}px`
      }
      return decodedValue
    } catch (error) {
      console.error('Failed to decode button border radius:', rawValue, error)
      return '8px'
    }
  })

  // Visibility settings (default to hidden for cleaner merchant embeds)
  const showCardDetails = computed(() => {
    const value = route.query.showCardDetails as string
    // Default to false - only show if explicitly set to 'true'
    return value === 'true'
  })

  const showCategoriesBar = computed(() => {
    const value = route.query.showCategoriesBar as string
    // Default to false - only show if explicitly set to 'true'
    return value === 'true'
  })

  const contentFontFamily = computed(() => themeFonts.value.contentFamily)
  const contentFontSrc = computed(() => themeFonts.value.contentSrc)
  const contentFontWeight = computed(() => themeFonts.value.contentWeight)
  const contentFontFormat = computed(() => themeFonts.value.contentFormat)
  const builderUiFontFamily = computed(() => themeFonts.value.builderUiFamily)
  const builderUiFontSrc = computed(() => themeFonts.value.builderUiSrc)
  const builderUiFontWeight = computed(() => themeFonts.value.builderUiWeight)
  const builderUiFontFormat = computed(() => themeFonts.value.builderUiFormat)

  const logoUrl = computed(() => {
    const rawUrl = route.query.logoUrl as string
    if (!rawUrl) return DEFAULT_LOGO_URL
    try {
      return decodeURIComponent(rawUrl)
    } catch (error) {
      console.error('Failed to decode logo URL:', rawUrl, error)
      return DEFAULT_LOGO_URL
    }
  })

  const backdropUrl = computed(() => {
    const rawUrl = route.query.backdropUrl as string
    if (!rawUrl) return ''
    try {
      return decodeURIComponent(rawUrl)
    } catch (error) {
      console.error('Failed to decode backdrop URL:', rawUrl, error)
      return ''
    }
  })

  // Front text position (for simple designs) - controls where text sits on the card
  // Values: top-left, top-center, top-right, middle-left, middle-center, middle-right, bottom-left, bottom-center, bottom-right
  const frontTextPosition = computed(() => {
    const rawValue = route.query.frontTextPosition as string
    if (!rawValue) return 'middle-center'
    try {
      return decodeURIComponent(rawValue)
    } catch {
      return 'middle-center'
    }
  })

  // Front text color override (for simple designs)
  const frontTextColor = computed(() => {
    const rawColor = route.query.frontTextColor as string
    if (!rawColor) return ''
    try {
      return decodeURIComponent(rawColor)
    } catch {
      return ''
    }
  })

  const backdropOverlayOpacity = computed(() => {
    const rawValue
      = (route.query.backdropOverlayOpacity as string)
        || (route.query.overlayOpacity as string)
    if (!rawValue) return null
    const parsed = Number(rawValue)
    if (Number.isNaN(parsed)) return null
    const normalized = parsed > 1 ? parsed / 100 : parsed
    return Math.min(1, Math.max(0, normalized))
  })

  /**
   * Helper to create transparent versions of accent color
   * Matches widget's accentColorTransparency pattern
   */
  const accentColorTransparency = (opacity: number) =>
    getTransparentColor(accentColor.value, opacity)

  /**
   * Button styling factory - uses explicit button colors instead of opacity
   * These return style objects that can be spread into :style bindings
   */
  const button = {
    /**
     * Primary button style - solid button color
     * Usage: <button :style="theme.button.primary()">Click me</button>
     */
    primary: (props?: Record<string, any>) => {
      return {
        color: buttonTextColor.value,
        boxShadow: '0px 8px 10px -6px #0000001A, 0px 20px 25px -5px #0000001A',
        backgroundColor: buttonBgColor.value,
        border: `1px solid ${buttonBgColor.value}`,
        borderRadius: buttonBorderRadius.value,
        ...props,
      }
    },

    /**
     * Secondary button style - lighter accent color with transparency (still uses opacity for secondary)
     * Usage: <button :style="theme.button.secondary()">Click me</button>
     */
    secondary: (props?: Record<string, any>) => {
      return {
        color: buttonTextColor.value,
        boxShadow: '0px 8px 10px -6px #0000001A, 0px 20px 25px -5px #0000001A',
        backdropFilter: 'blur(16px)',
        backgroundColor: accentColorTransparency(35),
        border: `1px solid ${accentColorTransparency(20)}`,
        borderRadius: buttonBorderRadius.value,
        ...props,
      }
    },

    /**
     * Ghost button style - transparent with border
     * Usage: <button :style="theme.button.ghost()">Click me</button>
     */
    ghost: (props?: Record<string, any>) => {
      return {
        color: accentColor.value,
        backgroundColor: 'transparent',
        border: `1px solid ${accentColorTransparency(30)}`,
        borderRadius: buttonBorderRadius.value,
        ...props,
      }
    },

    /**
     * Outline button style - transparent with solid border
     * Usage: <button :style="theme.button.outline()">Click me</button>
     */
    outline: (props?: Record<string, any>) => {
      return {
        color: accentColor.value,
        backgroundColor: 'transparent',
        border: `1px solid ${accentColor.value}`,
        borderRadius: buttonBorderRadius.value,
        ...props,
      }
    },

    /**
     * Soft button style - light background with no border
     * Usage: <button :style="theme.button.soft()">Click me</button>
     */
    soft: (props?: Record<string, any>) => {
      return {
        color: accentColor.value,
        backgroundColor: accentColorTransparency(15),
        border: 'none',
        borderRadius: buttonBorderRadius.value,
        ...props,
      }
    },
  }

  return {
    // Color properties (reactive, read from URL params)
    accentColor,
    textOnBlockColor,
    blockColor,
    buttonBgColor,
    buttonTextColor,
    buttonBorderRadius,
    accentColorTransparency,

    // Visibility settings
    showCardDetails,
    showCategoriesBar,
    contentFontFamily,
    contentFontSrc,
    contentFontWeight,
    contentFontFormat,
    builderUiFontFamily,
    builderUiFontSrc,
    builderUiFontWeight,
    builderUiFontFormat,
    fontFamily: contentFontFamily,
    fontSrc: contentFontSrc,
    fontWeight: contentFontWeight,
    fontFormat: contentFontFormat,
    logoUrl,
    backdropUrl,
    backdropOverlayOpacity,

    // Front text overrides (simple designs)
    frontTextPosition,
    frontTextColor,

    // Button factory functions
    button,
  }
}
